import { createRouter, createWebHistory } from 'vue-router';
import LoginPage from '@/pages/auth/LoginPage.vue';
import { AuthenticationService } from '@/services/AuthenticationService.ts';
import IndexPage from '@/pages/IndexPage.vue';
import RegisterPage from '@/pages/auth/RegisterPage.vue';
import RunPage from '@/pages/RunPage.vue';
import { syncToWebViewAction } from '@/actions/sync/SyncToWebViewAction.ts';
import { SYNC_TO_WEB_VIEW_KEYS } from '@/types/sync/SyncKeys.ts';
import TransferPage from '@/pages/auth/TransferPage.vue';
import OnboardingPage from '@/pages/OnboardingPage.vue';
import NotFoundPage from '@/pages/error/NotFoundPage.vue';
import NotAllowedPage from '@/pages/error/NotAllowedPage.vue';
import EditIndexPage from '@/pages/edit/EditIndexPage.vue';
import EditUpdatePage from '@/pages/edit/EditUpdatePage.vue';

export const enum ROUTES {
    // Index
    index = '/',
    onboarding = '/onboarding',
    // Auth
    authLogin = '/auth/login',
    authRegister = '/auth/register',
    // Edit mode
    editIndex = '/edit',
    editUpdate = '/edit/:profileConfigId',
    // Run
    runIndex = '/run/:sessionId/profile/:profileConfigId/step/:stepConfigId/:variableChainHash',
    // Web view
    webViewTransfer = '/web-view/transfer',
    // Error
    errorNotFound = '/error/not-found',
    errorNotAllowed = '/error/not-allowed',
}

export const BLOCKED_SYNC_ROUTES = [
    ROUTES.onboarding,
    ROUTES.authLogin,
    ROUTES.authRegister,
    ROUTES.editIndex,
    ROUTES.webViewTransfer,
];

const routeAuthGuard = () => {
    const isLoggedIn = AuthenticationService.instance.isLoggedIn();
    if (!isLoggedIn) {
        return { path: ROUTES.authLogin };
    }
};

const desktopAppOnlyGuard = () => {
    if (!window.electronApi) {
        return { path: ROUTES.errorNotFound };
    }
};

const webViewOnlyGuard = () => {
    if (window.electronApi) {
        return { path: ROUTES.errorNotFound };
    }
};

const routes = [
    // Index
    { name: ROUTES.index, path: ROUTES.index, component: IndexPage, beforeEnter: [routeAuthGuard] },
    {
        name: ROUTES.onboarding,
        path: ROUTES.onboarding,
        component: OnboardingPage,
        beforeEnter: [routeAuthGuard, desktopAppOnlyGuard],
    },
    // Auth
    { name: ROUTES.authLogin, path: ROUTES.authLogin, component: LoginPage },
    {
        name: ROUTES.authRegister,
        path: ROUTES.authRegister,
        component: RegisterPage,
        beforeEnter: [desktopAppOnlyGuard],
    },
    // Edit mode
    {
        name: ROUTES.editIndex,
        path: ROUTES.editIndex,
        component: EditIndexPage,
        beforeEnter: [desktopAppOnlyGuard],
    },
    {
        name: ROUTES.editUpdate,
        path: ROUTES.editUpdate,
        component: EditUpdatePage,
        beforeEnter: [desktopAppOnlyGuard],
    },
    // Run
    {
        name: ROUTES.runIndex,
        path: ROUTES.runIndex,
        component: RunPage,
    },
    // Web view
    {
        name: ROUTES.webViewTransfer,
        path: ROUTES.webViewTransfer,
        component: TransferPage,
        beforeEnter: [webViewOnlyGuard],
    },
    // Error pages
    {
        name: ROUTES.errorNotFound,
        path: ROUTES.errorNotFound,
        component: NotFoundPage,
    },
    {
        name: ROUTES.errorNotAllowed,
        path: ROUTES.errorNotAllowed,
        component: NotAllowedPage,
    },
    // Catch all
    {
        name: ROUTES.errorNotFound,
        path: '/:pathMatch(.*)*',
        component: NotFoundPage,
    },
];

export const router = createRouter({
    history: createWebHistory(),
    routes,
});

export const registerRouterSyncHook = () => {
    router.afterEach((to) => {
        if (!window.isElectron) {
            return;
        }

        syncToWebViewAction(
            SYNC_TO_WEB_VIEW_KEYS.routeChange,
            JSON.stringify({
                path: to.fullPath,
            }),
        );
    });
};
